"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useDebounce, useClickAway, useKeyPressEvent } from "react-use";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { escapeRegExp } from "@/lib/text/regex";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { GroupsRequestParams } from "@/entities/IGroup";
import { Mutable, NonNullableNotRequired } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
const MAX_RESULTS = 7;
const DEBOUNCE_MS = 200;
function HighlightedText(t0) {
  const $ = _c(8);
  const {
    text,
    query
  } = t0;
  let t1;
  let t2;
  if ($[0] !== query || $[1] !== text) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const trimmed = query.trim();
      if (!trimmed) {
        let t3;
        if ($[4] !== text) {
          t3 = <>{text}</>;
          $[4] = text;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        t2 = t3;
        break bb0;
      }
      const matcher = new RegExp(`(${escapeRegExp(trimmed)})`, "ig");
      const parts = text.split(matcher);
      t1 = parts.map(_temp);
    }
    $[0] = query;
    $[1] = text;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[6] !== t1) {
    t3 = <>{t1}</>;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp(part, index) {
  return index % 2 === 1 ? <span key={`${part}-${index}`} className="tw-text-primary-300">{part}</span> : <span key={`${part}-${index}`} className="tw-text-inherit">{part}</span>;
}
export default function CreateWaveGroupSearchField(t0) {
  const $ = _c(89);
  const {
    label,
    defaultLabel,
    disabled,
    selectedGroup,
    onSelect
  } = t0;
  const inputRef = useRef(null);
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const wrapperRef = useRef(null);
  const [inputValue, setInputValue] = useState(selectedGroup?.name ?? "");
  const [searchCriteria, setSearchCriteria] = useState(selectedGroup?.name ?? "");
  const [debouncedValue, setDebouncedValue] = useState(selectedGroup?.name ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  let t1;
  if ($[0] !== searchCriteria) {
    t1 = () => {
      setDebouncedValue(searchCriteria.trim());
    };
    $[0] = searchCriteria;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== searchCriteria) {
    t2 = [searchCriteria];
    $[2] = searchCriteria;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useDebounce(t1, DEBOUNCE_MS, t2);
  const t3 = debouncedValue || null;
  let t4;
  if ($[4] !== t3) {
    t4 = [QueryKey.GROUPS, {
      group_name: t3
    }];
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== debouncedValue) {
    t5 = async () => {
      const params = {};
      if (debouncedValue) {
        params.group_name = debouncedValue;
      }
      return await commonApiFetch({
        endpoint: "groups",
        params
      });
    };
    $[6] = debouncedValue;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = isOpen && !disabled;
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      placeholderData: keepPreviousData
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    data,
    isFetching
  } = useQuery(t7);
  let t8;
  if ($[12] !== data) {
    t8 = data ?? [];
    $[12] = data;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = t8.slice(0, MAX_RESULTS);
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const suggestions = t9;
  let t10;
  if ($[16] !== onSelect) {
    t10 = () => {
      setInputValue("");
      setSearchCriteria("");
      onSelect(null);
      setIsOpen(true);
      setActiveIndex(-1);
      inputRef.current?.focus();
    };
    $[16] = onSelect;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const clearSelection = t10;
  let t11;
  let t12;
  if ($[18] !== selectedGroup) {
    t11 = () => {
      if (selectedGroup) {
        setInputValue(selectedGroup.name);
        setSearchCriteria(selectedGroup.name);
      } else {
        setInputValue("");
        setSearchCriteria("");
      }
    };
    t12 = [selectedGroup];
    $[18] = selectedGroup;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  if ($[21] !== isOpen) {
    t13 = () => {
      if (!isOpen) {
        return;
      }
      setIsOpen(false);
      setActiveIndex(-1);
    };
    $[21] = isOpen;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  useClickAway(wrapperRef, t13);
  let t14;
  if ($[23] !== isOpen) {
    t14 = () => {
      if (!isOpen) {
        return;
      }
      setIsOpen(false);
      setActiveIndex(-1);
    };
    $[23] = isOpen;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  useKeyPressEvent("Escape", t14);
  let t15;
  if ($[25] !== disabled) {
    t15 = () => {
      if (disabled) {
        return;
      }
      setIsOpen(true);
      setActiveIndex(-1);
    };
    $[25] = disabled;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const onInputFocus = t15;
  let t16;
  if ($[27] !== onSelect || $[28] !== selectedGroup) {
    t16 = value => {
      setInputValue(value);
      setSearchCriteria(value);
      setIsOpen(true);
      setActiveIndex(-1);
      if (selectedGroup) {
        onSelect(null);
      }
    };
    $[27] = onSelect;
    $[28] = selectedGroup;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const handleInputChange = t16;
  let t17;
  if ($[30] !== onSelect) {
    t17 = group => {
      setInputValue(group.name);
      setSearchCriteria(group.name);
      onSelect(group);
      setIsOpen(false);
      setActiveIndex(-1);
      inputRef.current?.focus();
    };
    $[30] = onSelect;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const onOptionSelect = t17;
  let t18;
  if ($[32] !== activeIndex || $[33] !== isOpen || $[34] !== onOptionSelect || $[35] !== suggestions) {
    t18 = event => {
      if (!isOpen) {
        if (event.key === "ArrowDown") {
          setIsOpen(true);
          setActiveIndex(0);
          event.preventDefault();
        }
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex(prev => {
          const nextIndex = prev + 1;
          if (nextIndex >= suggestions.length) {
            return suggestions.length ? 0 : -1;
          }
          return nextIndex;
        });
      } else {
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setActiveIndex(prev_0 => {
            if (suggestions.length === 0) {
              return -1;
            }
            if (prev_0 <= 0) {
              return suggestions.length - 1;
            }
            return prev_0 - 1;
          });
        } else {
          if (event.key === "Enter") {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
              event.preventDefault();
              onOptionSelect(suggestions[activeIndex]);
            }
          } else {
            if (event.key === "Escape") {
              event.preventDefault();
              setIsOpen(false);
              setActiveIndex(-1);
            }
          }
        }
      }
    };
    $[32] = activeIndex;
    $[33] = isOpen;
    $[34] = onOptionSelect;
    $[35] = suggestions;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const handleKeyDown = t18;
  const showNoResults = !isFetching && isOpen && suggestions.length === 0;
  const helperText = selectedGroup ? `Selected: ${selectedGroup.name}` : `Default: ${defaultLabel}`;
  let t19;
  if ($[37] !== inputValue) {
    t19 = inputValue.trim();
    $[37] = inputValue;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const hasValue = t19.length > 0;
  const showClearButton = (hasValue || !!selectedGroup) && !disabled;
  const t20 = disabled ? "tw-ring-iron-700 tw-text-iron-500 tw-caret-iron-500 placeholder:tw-text-iron-500 tw-bg-iron-800" : "tw-ring-iron-700 focus:tw-border-blue-500 tw-caret-primary-400 focus:tw-ring-primary-400 hover:tw-ring-iron-650 placeholder:tw-text-iron-500";
  const t21 = hasValue || selectedGroup ? "focus:tw-text-white tw-text-primary-400" : "tw-text-white";
  let t22;
  if ($[39] !== t20 || $[40] !== t21) {
    t22 = ["tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-appearance-none tw-font-medium tw-peer tw-pl-10 tw-pr-4 tw-bg-iron-900 tw-shadow-sm tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out tw-text-base tw-pt-3 tw-pb-3", t20, t21];
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const inputClasses = t22.join(" ");
  const t23 = activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;
  let t24;
  if ($[42] !== handleInputChange) {
    t24 = event_0 => handleInputChange(event_0.target.value);
    $[42] = handleInputChange;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== disabled || $[45] !== handleKeyDown || $[46] !== inputClasses || $[47] !== inputId || $[48] !== inputValue || $[49] !== isOpen || $[50] !== listboxId || $[51] !== onInputFocus || $[52] !== t23 || $[53] !== t24) {
    t25 = <input ref={inputRef} id={inputId} type="text" role="combobox" aria-autocomplete="list" aria-expanded={isOpen} aria-controls={listboxId} aria-disabled={disabled} aria-activedescendant={t23} value={inputValue} onFocus={onInputFocus} onChange={t24} onKeyDown={handleKeyDown} placeholder=" " disabled={disabled} className={inputClasses} autoComplete="off" />;
    $[44] = disabled;
    $[45] = handleKeyDown;
    $[46] = inputClasses;
    $[47] = inputId;
    $[48] = inputValue;
    $[49] = isOpen;
    $[50] = listboxId;
    $[51] = onInputFocus;
    $[52] = t23;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <svg className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-3.5 tw-h-5 tw-w-5 tw-text-iron-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== clearSelection || $[57] !== showClearButton) {
    t27 = showClearButton && <svg onClick={clearSelection} className="tw-cursor-pointer tw-absolute tw-right-3 tw-top-3.5 tw-h-5 tw-w-5 tw-text-iron-400 hover:tw-text-error tw-transition tw-duration-300 tw-ease-out" viewBox="0 0 24 24" fill="none" aria-hidden="true" aria-label="Clear selected group" xmlns="http://www.w3.org/2000/svg"><path d="M17 7L7 17M7 7L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[56] = clearSelection;
    $[57] = showClearButton;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  const t28 = `tw-absolute tw-rounded-lg tw-cursor-text tw-font-medium tw-text-base ${disabled ? "tw-text-iron-400" : "tw-text-iron-500"} tw-duration-300 tw-transform -tw-translate-y-4 tw-scale-75 tw-top-2 tw-z-10 tw-origin-[0] tw-bg-iron-900 peer-focus:tw-bg-iron-900 tw-ml-7 tw-px-2 peer-focus:tw-px-2 peer-focus:tw-text-primary-400 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:-tw-translate-y-1/2 peer-placeholder-shown:tw-top-1/2 peer-focus:tw-top-2 peer-focus:tw-scale-75 peer-focus:-tw-translate-y-4 rtl:peer-focus:tw-translate-x-1/4 rtl:peer-focus:tw-left-auto tw-start-1`;
  let t29;
  if ($[59] !== inputId || $[60] !== label || $[61] !== t28) {
    t29 = <label htmlFor={inputId} className={t28}>{label}</label>;
    $[59] = inputId;
    $[60] = label;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t25 || $[64] !== t27 || $[65] !== t29) {
    t30 = <div className="tw-relative">{t25}{t26}{t27}{t29}</div>;
    $[63] = t25;
    $[64] = t27;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== activeIndex || $[68] !== disabled || $[69] !== helperText || $[70] !== isFetching || $[71] !== isOpen || $[72] !== listboxId || $[73] !== onOptionSelect || $[74] !== searchCriteria || $[75] !== selectedGroup?.id || $[76] !== showNoResults || $[77] !== suggestions) {
    t31 = isOpen && !disabled && <motion.div className="tw-absolute tw-z-50 tw-mt-1.5 tw-w-full tw-rounded-lg tw-shadow-xl tw-bg-iron-800 tw-ring-1 tw-ring-black tw-ring-opacity-5" initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.2
    }}><div className="tw-absolute tw-overflow-hidden tw-w-full tw-rounded-md tw-bg-iron-800 tw-shadow-2xl tw-ring-1 tw-ring-white/10"><div className="tw-py-1 tw-flow-root tw-overflow-x-hidden tw-overflow-y-auto tw-max-h-64"><ul id={listboxId} role="listbox" className="tw-flex tw-flex-col tw-gap-y-1 tw-px-2 tw-mx-0 tw-mb-0 tw-list-none">{isFetching ? <li className="tw-flex tw-items-center tw-justify-center tw-py-4"><CircleLoader size={CircleLoaderSize.SMALL} /></li> : suggestions.length > 0 ? suggestions.map((group_0, index) => {
              const isActive = index === activeIndex;
              const isSelected = selectedGroup?.id === group_0.id;
              const optionStateClasses = isActive || isSelected ? "tw-bg-iron-800 tw-text-white" : "tw-text-white hover:tw-bg-iron-800";
              return <li key={group_0.id} id={`${listboxId}-option-${index}`} role="option" aria-selected={isSelected} className={`tw-py-2 tw-w-full tw-flex tw-items-center tw-justify-between tw-text-sm tw-font-medium tw-rounded-lg tw-relative tw-select-none tw-px-2 ${optionStateClasses}`} onMouseEnter={() => setActiveIndex(index)} onMouseDown={_temp2} onClick={() => onOptionSelect(group_0)}><div className="tw-flex tw-flex-col tw-gap-y-1 tw-w-full"><span className="tw-text-sm tw-font-semibold tw-text-iron-50 tw-truncate"><HighlightedText text={group_0.name} query={searchCriteria} /></span><span className="tw-text-xs tw-text-iron-400 tw-truncate"><HighlightedText text={`@${group_0.created_by?.handle ?? "unknown"}`} query={searchCriteria} /></span></div></li>;
            }) : <li className="tw-py-2 tw-w-full tw-text-sm tw-font-medium tw-text-white tw-rounded-lg tw-relative tw-select-none tw-px-4">{showNoResults ? "No groups found" : helperText}</li>}</ul></div></div></motion.div>;
    $[67] = activeIndex;
    $[68] = disabled;
    $[69] = helperText;
    $[70] = isFetching;
    $[71] = isOpen;
    $[72] = listboxId;
    $[73] = onOptionSelect;
    $[74] = searchCriteria;
    $[75] = selectedGroup?.id;
    $[76] = showNoResults;
    $[77] = suggestions;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== t31) {
    t32 = <AnimatePresence mode="wait" initial={false}>{t31}</AnimatePresence>;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t30 || $[82] !== t32) {
    t33 = <div className="tw-group tw-w-full tw-relative">{t30}{t32}</div>;
    $[81] = t30;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== helperText) {
    t34 = <p className="tw-text-xs tw-font-medium tw-text-iron-400 tw-mb-0">{helperText}</p>;
    $[84] = helperText;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t33 || $[87] !== t34) {
    t35 = <div className="tw-flex tw-flex-col tw-gap-y-2" ref={wrapperRef}>{t33}{t34}</div>;
    $[86] = t33;
    $[87] = t34;
    $[88] = t35;
  } else {
    t35 = $[88];
  }
  return t35;
}
function _temp2(event_1) {
  return event_1.preventDefault();
}